import React from 'react';
import { Typography, Row, Col, Card, Statistic, Table, Tag, Empty, Button, Spin } from 'antd';
import { PlusOutlined, FileTextOutlined, ExclamationCircleOutlined, CameraOutlined, RiseOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { dashboardApi } from '../api/endpoints';
import { STATUS_LABELS, STATUS_COLORS, PLATFORM_LABELS, PLATFORM_COLORS } from '../constants/status';
import { URGENCY_BORDER } from '../styles/tokens';
import type { SnsPost } from '../api/types';

const { Title } = Typography;

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: kpi, isLoading: kpiLoading } = useQuery({
    queryKey: ['dashboard', 'kpi'],
    queryFn: () => dashboardApi.kpi().then(r => (r.data as any).data ?? r.data),
  });
  const { data: upcoming, isLoading: upcomingLoading } = useQuery({
    queryKey: ['dashboard', 'upcoming'],
    queryFn: () => dashboardApi.upcoming().then(r => (r.data as any).data ?? r.data),
  });

  const columns = [
    { title: '日付', dataIndex: 'scheduled_date', key: 'date', width: 120 },
    {
      title: '店舗', key: 'store',
      render: (_: unknown, record: SnsPost) => record.store?.name ?? '-',
    },
    {
      title: 'プラットフォーム', dataIndex: 'platform', key: 'platform', width: 140,
      render: (platform: string) => (
        <Tag color={PLATFORM_COLORS[platform as keyof typeof PLATFORM_COLORS]}>
          {PLATFORM_LABELS[platform as keyof typeof PLATFORM_LABELS]}
        </Tag>
      ),
    },
    {
      title: 'ステータス', dataIndex: 'status', key: 'status', width: 150,
      render: (status: string) => (
        <Tag color={STATUS_COLORS[status as keyof typeof STATUS_COLORS]}>
          {STATUS_LABELS[status as keyof typeof STATUS_LABELS]}
        </Tag>
      ),
    },
    { title: '原稿', dataIndex: 'caption', key: 'caption', ellipsis: true },
  ];

  return (
    <div>
      <Title level={4}>ダッシュボード</Title>

      <Spin spinning={kpiLoading}>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card className="kpi-card" hoverable style={{ cursor: 'pointer' }} onClick={() => navigate('/posts')}>
              <Statistic title="今月の投稿数" value={kpi?.total_posts ?? 0} prefix={<FileTextOutlined />} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="kpi-card" hoverable style={{ cursor: 'pointer', ...((kpi?.pending_tasks ?? 0) > 0 ? URGENCY_BORDER.danger : URGENCY_BORDER.none) }} onClick={() => navigate('/posts?status=draft')}>
              <Statistic title="未対応タスク" value={kpi?.pending_tasks ?? 0} prefix={<ExclamationCircleOutlined />} valueStyle={{ color: (kpi?.pending_tasks ?? 0) > 0 ? '#cf1322' : undefined }} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="kpi-card" hoverable style={{ cursor: 'pointer', ...((kpi?.waiting_photos ?? 0) > 0 ? URGENCY_BORDER.warning : URGENCY_BORDER.none) }} onClick={() => navigate('/posts?status=photo_requested')}>
              <Statistic title="写真待ち" value={kpi?.waiting_photos ?? 0} prefix={<CameraOutlined />} valueStyle={{ color: (kpi?.waiting_photos ?? 0) > 0 ? '#fa8c16' : undefined }} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="kpi-card" hoverable style={{ cursor: 'pointer' }} onClick={() => navigate('/posts')}>
              <Statistic title="進捗率" value={kpi?.progress_rate ?? 0} prefix={<RiseOutlined />} suffix="%" />
            </Card>
          </Col>
        </Row>
      </Spin>

      <Card
        title="今週の投稿予定"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/posts')}>新規投稿</Button>}
      >
        {upcomingLoading ? (
          <Spin />
        ) : (upcoming?.length ?? 0) === 0 ? (
          <Empty description="まだ今週の投稿予定がありません">
            <Button type="primary" onClick={() => navigate('/posts')}>最初の投稿を作成</Button>
          </Empty>
        ) : (
          <Table
            dataSource={upcoming}
            columns={columns}
            rowKey="id"
            pagination={false}
            rowClassName={() => 'clickable-row'}
            onRow={(record) => ({ onClick: () => navigate(`/posts/${record.id}`), style: { cursor: 'pointer' } })}
          />
        )}
      </Card>
    </div>
  );
};

export default DashboardPage;
